import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { Dialog, DialogContent, DialogTitle } from '../../ui/dialog';
import { FileIcon, RefreshCw, FileSpreadsheet, ChevronDown, ChevronRight, Calendar, PanelLeftClose, PanelLeft, BarChart3, Maximize2, Receipt, Users } from 'lucide-react';
import { PaySummarySpreadsheet } from './pay-summary/PaySummarySpreadsheet';
import { ModelPaySummarySpreadsheet } from './model-pay-summary/ModelPaySummarySpreadsheet';
import { ModelPPVSpreadsheet } from './ModelPPVSpreadsheet';
import { InvoicesView } from './InvoicesView';
import { ModelStatisticsSpreadsheet } from './ModelStatisticsSpreadsheet';
import { StaffReportSpreadsheet } from './StaffReportSpreadsheet';
import { ModelEarningsSpreadsheet } from './ModelEarningsSpreadsheet';
import { Breadcrumbs } from '../../ui/breadcrumbs';
import { cn } from '../../../lib/utils';
import { DateTime } from 'luxon';
import { useAuth } from '../../../contexts/AuthContext';
import { Permissions } from '@shiftly/shared/src/permissions';
import FanGainGroupsView from './FanGainGroupsView';
import UserModelAssignmentView from './UserModelAssignmentView';
import { VisuallyHidden } from '../../ui/visually-hidden';
import { FanGainPerAdSpreadsheet } from './FanGainPerAdSpreadsheet';
import FanGainGGSpreadsheet from './fan-gain/gg/FanGainGGSpreadsheet';
import DailyFanGainSpreadsheet from './fan-gain/daily/DailyFanGainSpreadsheet';

type DocumentType = 'pay-summary' | 'shift-report' | 'pay-report' | 'model-pay-summary' | 'model-ppv' | 'invoices' | 'staff-report' | 'model-statistics' | 'fan-gain-gg-swaps' | 'fan-gain-groups' | 'fan-gain-totals' | 'user-model-assignment' | 'model-earnings-report' | 'target-earnings' | 'fan-gain-per-ad';

interface PayDocument {
  id: string;
  type: DocumentType;
  name: string;
  month?: number;
  year?: number;
  category?: string;
  isMonthIndependent?: boolean;
}

interface DocumentDefinition {
  type: DocumentType;
  name: string;
  icon: React.ReactNode;
  showFullscreen: boolean;
  requiredPermissions: string[];
  category?: string;
  isMonthIndependent?: boolean;
  availableFor?: 'all' | 'current-month-only' | 'non-current-month-only';
  route?: string;
}

interface GroupedDocuments {
  monthIndependent?: PayDocument[];
  yearlyDocuments: {
    [year: string]: {
      [month: string]: {
        categories: {
          [categoryName: string]: PayDocument[];
        };
        uncategorized: PayDocument[];
      };
    };
  };
}

const DOCUMENT_REGISTRY: DocumentDefinition[] = [
  {
    type: 'pay-summary',
    name: 'Pay Summary',
    icon: <FileSpreadsheet className="h-4 w-4 mr-2" />,
    showFullscreen: true,
    requiredPermissions: [Permissions.SHEETS_PAY_SUMMARY],
    availableFor: 'non-current-month-only'
  },
  /*{
    type: 'shift-report',
    name: 'Shift Report',
    icon: <BarChart3 className="h-4 w-4 mr-2" />,
    showFullscreen: true,
    requiredPermissions: [Permissions.PAY_PAYMENT_FILES],
    availableFor: 'non-current-month-only'
  },
  {
    type: 'pay-report',
    name: 'Pay Report',
    icon: <FileSpreadsheetIcon className="h-4 w-4 mr-2" />,
    showFullscreen: true,
    requiredPermissions: [Permissions.PAY_PAYMENT_FILES],
    availableFor: 'non-current-month-only'
  },*/
  {
    type: 'model-pay-summary',
    name: 'Model Pay Summary',
    icon: <FileSpreadsheet className="h-4 w-4 mr-2" />,
    showFullscreen: true,
    requiredPermissions: [Permissions.SHEETS_MODEL_PAY_SUMMARY],
    availableFor: 'non-current-month-only'
  },
  {
    type: 'invoices',
    name: 'Invoices',
    icon: <Receipt className="h-4 w-4 mr-2" />,
    showFullscreen: true,
    requiredPermissions: [Permissions.SHEETS_INVOICES],
    availableFor: 'non-current-month-only'
  },
  {
    type: 'model-ppv',
    name: 'Model PPV Report',
    icon: <BarChart3 className="h-4 w-4 mr-2" />,
    showFullscreen: true,
    requiredPermissions: [Permissions.SHEETS_MODEL_PPV],
    category: 'Performance',
    availableFor: 'all'
  },
  {
    type: 'staff-report',
    name: 'Staff Report',
    icon: <Users className="h-4 w-4 mr-2" />,
    showFullscreen: true,
    requiredPermissions: [Permissions.SHEETS_STAFF_REPORT],
    isMonthIndependent: true,
    route: '/payment-files/staff-report'
  },
  {
    type: 'model-earnings-report',
    name: 'Model Earnings Report',
    icon: <BarChart3 className="h-4 w-4 mr-2" />,
    showFullscreen: true,
    requiredPermissions: [Permissions.SHEETS_MODEL_EARNINGS_REPORT],
    isMonthIndependent: true,
    route: '/payment-files/model-earnings-report'
  },
  {
    type: 'model-statistics',
    name: 'Model Statistics',
    icon: <BarChart3 className="h-4 w-4 mr-2" />,
    showFullscreen: true,
    requiredPermissions: [Permissions.SHEETS_MODEL_STATISTICS],
    category: 'Performance',
    availableFor: 'all'
  },
  {
    type: 'fan-gain-gg-swaps',
    name: 'GG Swaps',
    icon: <BarChart3 className="h-4 w-4 mr-2" />,
    showFullscreen: true,
    requiredPermissions: [Permissions.SHEETS_FAN_GAIN],
    category: 'Fan Gain',
    availableFor: 'all'
  },
  {
    type: 'fan-gain-groups',
    name: 'Groups',
    icon: <Users className="h-4 w-4 mr-2" />,
    showFullscreen: true,
    requiredPermissions: [Permissions.SHEETS_FAN_GAIN],
    category: 'Fan Gain',
    availableFor: 'all'
  },
  {
    type: 'fan-gain-totals',
    name: 'Totals',
    icon: <BarChart3 className="h-4 w-4 mr-2" />,
    showFullscreen: true,
    requiredPermissions: [Permissions.SHEETS_FAN_GAIN],
    category: 'Fan Gain',
    availableFor: 'all'
  },
  {
    type: 'user-model-assignment',
    name: 'Model Assignments',
    icon: <Users className="h-4 w-4 mr-2" />,
    showFullscreen: true,
    requiredPermissions: [Permissions.SHEETS_FAN_GAIN],
    category: 'Fan Gain',
    availableFor: 'all'
  },
  {
    type: 'fan-gain-per-ad',
    name: 'Fan Gain Per Ad',
    icon: <BarChart3 className="h-4 w-4 mr-2" />,
    showFullscreen: true,
    requiredPermissions: [Permissions.SHEETS_FAN_GAIN_PER_AD],
    isMonthIndependent: true,
    route: '/payment-files/fan-gain-per-ad'
  }
];

const getDocumentDefinition = (type: DocumentType): DocumentDefinition | undefined => {
  return DOCUMENT_REGISTRY.find(doc => doc.type === type);
};


const getMonthIndependentDocuments = (): DocumentDefinition[] => {
  return DOCUMENT_REGISTRY.filter(doc => doc.isMonthIndependent);
};

const getDocumentsForPeriod = (isCurrentMonth: boolean): DocumentDefinition[] => {
  return DOCUMENT_REGISTRY.filter(doc => {
    if (doc.isMonthIndependent) return false;

    switch (doc.availableFor) {
      case 'current-month-only':
        return isCurrentMonth;
      case 'non-current-month-only':
        return !isCurrentMonth;
      case 'all':
      default:
        return true;
    }
  });
};

const getDocumentCategory = (type: DocumentType): string | undefined => {
  const definition = getDocumentDefinition(type);
  return definition?.category;
};

const getPreviousMonth = () => {
  const date = DateTime.now().minus({ month: 1 }).startOf('month');
  return {
    month: date.month,
    year: date.year
  };
};

const getCurrentMonth = () => {
  const date = DateTime.now().startOf('month');
  return {
    month: date.month,
    year: date.year
  };
};

const getAvailablePeriods = () => {
  const startDate = DateTime.fromObject({ month: 1, year: 2025 });
  const endDate = DateTime.now();

  const periods = [];
  let currentDate = startDate;

  while (currentDate.toMillis() <= endDate.toMillis()) {
    const month = currentDate.month;
    const year = currentDate.year;
    periods.push({ month, year });
    currentDate = currentDate.plus({ month: 1 });
  }

  return periods;
}

const createDocumentFromParams = (yearParam: string, monthParam: string, typeParam: string): PayDocument | null => {
  const yearNum = parseInt(yearParam);
  const monthNum = parseInt(monthParam);

  if (isNaN(yearNum) || isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
    return null;
  }

  const definition = getDocumentDefinition(typeParam as DocumentType);
  if (!definition) {
    return null;
  }

  const isAvailable = getAvailablePeriods().some(
    period => period.year === yearNum && period.month === monthNum
  );

  if (!isAvailable) {
    return null;
  }

  return {
    id: `${typeParam}-${yearNum}-${monthNum}`,
    type: typeParam as DocumentType,
    name: definition.name,
    month: monthNum,
    year: yearNum,
    category: definition.category
  };
};

const hasAccessToDocumentType = (type: DocumentType, permissions: string[]): boolean => {
  const definition = getDocumentDefinition(type);
  if (!definition) return false;

  return definition.requiredPermissions.some(permission => permissions.includes(permission));
};

const getDefaultDocument = (permissions: string[], previousMonth: { month: number; year: number }, currentMonth: { month: number; year: number }): PayDocument => {
  const availableDocuments = DOCUMENT_REGISTRY.filter(def =>
    hasAccessToDocumentType(def.type, permissions)
  );

  if (availableDocuments.length === 0) {
    throw new Error('No valid permissions to access any documents');
  }

  const paySummary = availableDocuments.find(doc => doc.type === 'pay-summary');
  if (paySummary) {
    return {
      id: `pay-summary-${previousMonth.year}-${previousMonth.month}`,
      type: 'pay-summary',
      name: paySummary.name,
      month: previousMonth.month,
      year: previousMonth.year,
      category: paySummary.category
    };
  }

  const firstDoc = availableDocuments[0];
  const targetMonth = firstDoc.type === 'fan-gain-gg-swaps' ? currentMonth : previousMonth;

  return {
    id: `${firstDoc.type}-${targetMonth.year}-${targetMonth.month}`,
    type: firstDoc.type,
    name: firstDoc.name,
    month: targetMonth.month,
    year: targetMonth.year,
    category: firstDoc.category
  };
};

export function PaymentFilesManager() {
  const previousMonth = getPreviousMonth();
  const currentMonth = getCurrentMonth();
  const { year, month, type } = useParams<{ year: string; month: string; type: string }>();
  const navigate = useNavigate();
  const { permissions } = useAuth();
  const location = useLocation();

  const [selectedDocument, setSelectedDocument] = useState<PayDocument | null>(() => {
    const monthIndependentDoc = getMonthIndependentDocuments().find(def =>
      def.route && location.pathname === def.route
    );
    if (monthIndependentDoc) {
      return {
        id: monthIndependentDoc.type,
        type: monthIndependentDoc.type,
        name: monthIndependentDoc.name,
        isMonthIndependent: true,
        category: monthIndependentDoc.category
      };
    }

    if (year && month && type) {
      const docFromUrl = createDocumentFromParams(year, month, type);
      if (docFromUrl && hasAccessToDocumentType(docFromUrl.type, permissions)) {
        return docFromUrl;
      }
    }

    try {
      return getDefaultDocument(permissions, previousMonth, currentMonth);
    } catch (error) {
      return null;
    }
  });

  const [expandedYears, setExpandedYears] = useState<Set<string>>(() => {
    const initialYear = selectedDocument?.year?.toString() || previousMonth.year.toString();
    return new Set([initialYear]);
  });

  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(() => {
    const initialMonth = selectedDocument ? `${selectedDocument.year}-${selectedDocument.month}` : `${previousMonth.year}-${previousMonth.month}`;
    return new Set([initialMonth]);
  });

  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(() => {
    if (selectedDocument) {
      const category = getDocumentCategory(selectedDocument.type);
      if (category) {
        const categoryKey = `${selectedDocument.year}-${selectedDocument.month}-${category}`;
        return new Set([categoryKey]);
      }
    }
    return new Set();
  });

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);

  const availablePeriods = useMemo(() => {
    return getAvailablePeriods();
  }, []);

  useEffect(() => {
    if (year && month && type) {
      const docFromUrl = createDocumentFromParams(year, month, type);
      if (docFromUrl && (!selectedDocument || selectedDocument.id !== docFromUrl.id)) {
        setSelectedDocument(docFromUrl);

        if (docFromUrl.year !== undefined) {
          setExpandedYears(prev => new Set([...prev, docFromUrl.year!.toString()]));
        }
        if (docFromUrl.year !== undefined && docFromUrl.month !== undefined) {
          setExpandedMonths(prev => new Set([...prev, `${docFromUrl.year}-${docFromUrl.month}`]));
        }

        const category = getDocumentCategory(docFromUrl.type);
        if (category) {
          const categoryKey = `${docFromUrl.year}-${docFromUrl.month}-${category}`;
          setExpandedCategories(prev => new Set([...prev, categoryKey]));
        }
      }
    }
  }, [year, month, type, selectedDocument]);

  useEffect(() => {
    if (!year && !month && !type && selectedDocument && !selectedDocument.isMonthIndependent) {
      navigate(`/payment-files/${selectedDocument.year}/${selectedDocument.month}/${selectedDocument.type}`, { replace: true });
    } else if (year && month && type) {
      const docFromUrl = createDocumentFromParams(year, month, type);
      if (!docFromUrl) {
        navigate(`/payment-files/${previousMonth.year}/${previousMonth.month}/pay-summary`, { replace: true });
      }
    }
  }, [year, month, type, selectedDocument, navigate, previousMonth]);

  const months = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  const groupedDocuments = useMemo(() => {
    const grouped: GroupedDocuments = {
      monthIndependent: [],
      yearlyDocuments: {}
    };
    const currentDate = DateTime.now();
    const currentMonth = currentDate.month;
    const currentYear = currentDate.year;

    const monthIndependentDocs = getMonthIndependentDocuments().filter(def =>
      hasAccessToDocumentType(def.type, permissions)
    );

    grouped.monthIndependent = monthIndependentDocs.map(def => ({
      id: def.type,
      type: def.type,
      name: def.name,
      isMonthIndependent: true,
      category: def.category
    }));

    availablePeriods.forEach(({ month, year }) => {
      const yearStr = year.toString();
      const monthStr = month.toString();

      if (!grouped.yearlyDocuments[yearStr]) {
        grouped.yearlyDocuments[yearStr] = {};
      }
      if (!grouped.yearlyDocuments[yearStr][monthStr]) {
        grouped.yearlyDocuments[yearStr][monthStr] = {
          categories: {},
          uncategorized: []
        };
      }

      const isCurrentMonth = year === currentYear && month === currentMonth;

      const availableDefinitions = getDocumentsForPeriod(isCurrentMonth).filter(def =>
        hasAccessToDocumentType(def.type, permissions)
      );

      const documents: PayDocument[] = availableDefinitions.map(def => ({
        id: `${def.type}-${year}-${month}`,
        type: def.type,
        name: def.name,
        month,
        year,
        category: def.category
      }));

      documents.forEach(doc => {
        const category = doc.category;
        if (category) {
          if (!grouped.yearlyDocuments[yearStr][monthStr].categories[category]) {
            grouped.yearlyDocuments[yearStr][monthStr].categories[category] = [];
          }
          grouped.yearlyDocuments[yearStr][monthStr].categories[category].push(doc);
        } else {
          grouped.yearlyDocuments[yearStr][monthStr].uncategorized.push(doc);
        }
      });
    });

    return grouped;
  }, [availablePeriods, permissions]);

  const handleRefresh = () => {
    window.location.reload();
  };

  const toggleYearExpanded = (year: string) => {
    const newExpanded = new Set(expandedYears);
    if (newExpanded.has(year)) {
      newExpanded.delete(year);
    } else {
      newExpanded.add(year);
    }
    setExpandedYears(newExpanded);
  };

  const toggleMonthExpanded = (year: string, month: string) => {
    const monthKey = `${year}-${month}`;
    const newExpanded = new Set(expandedMonths);
    if (newExpanded.has(monthKey)) {
      newExpanded.delete(monthKey);
    } else {
      newExpanded.add(monthKey);
    }
    setExpandedMonths(newExpanded);
  };

  const toggleCategoryExpanded = (year: string, month: string, category: string) => {
    const categoryKey = `${year}-${month}-${category}`;
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryKey)) {
      newExpanded.delete(categoryKey);
    } else {
      newExpanded.add(categoryKey);
    }
    setExpandedCategories(newExpanded);
  };

  const handleDocumentSelect = (document: PayDocument) => {
    if (!hasAccessToDocumentType(document.type, permissions)) {
      return;
    }

    if (document.isMonthIndependent) {
      const definition = getDocumentDefinition(document.type);
      if (definition?.route) {
        navigate(definition.route);
      }
      setSelectedDocument(document);
    } else {
      navigate(`/payment-files/${document.year}/${document.month}/${document.type}`);
    }
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const openFullscreen = () => {
    setIsFullscreenOpen(true);
  };

  const renderSpreadsheetContent = (document: PayDocument) => {
    switch (document.type) {
      case 'pay-summary':
        return (
          <PaySummarySpreadsheet
            defaultMonth={document.month!}
            defaultYear={document.year!}
          />
        );
      /*case 'shift-report':
        return (
          <ShiftReportSpreadsheet
            defaultMonth={document.month!}
            defaultYear={document.year!}
          />
        );
      case 'pay-report':
        return (
          <PayReportSpreadsheet
            defaultMonth={document.month!}
            defaultYear={document.year!}
          />
        );*/
      case 'model-pay-summary':
        return (
          <ModelPaySummarySpreadsheet
            defaultMonth={document.month!}
            defaultYear={document.year!}
          />
        );
      case 'model-ppv':
        return (
          <ModelPPVSpreadsheet
            defaultMonth={document.month!}
            defaultYear={document.year!}
          />
        );
      case 'invoices':
        return (
          <InvoicesView
            defaultMonth={document.month!}
            defaultYear={document.year!}
          />
        );
      case 'staff-report':
        return (
          <StaffReportSpreadsheet />
        );
      case 'model-earnings-report':
        return (
          <ModelEarningsSpreadsheet />
        );
      case 'model-statistics':
        return (
          <ModelStatisticsSpreadsheet
            defaultMonth={document.month!}
            defaultYear={document.year!}
          />
        );
      case 'fan-gain-gg-swaps':
        return (
          <FanGainGGSpreadsheet
            defaultMonth={document.month!}
            defaultYear={document.year!}
          />
        );
      case 'fan-gain-groups':
        return (
          <FanGainGroupsView />
        );
      case 'fan-gain-totals':
        return (
          <DailyFanGainSpreadsheet
            defaultMonth={document.month!}
            defaultYear={document.year!}
          />
        );
      case 'user-model-assignment':
        return (
          <UserModelAssignmentView />
        );
      case 'fan-gain-per-ad':
        return (
          <FanGainPerAdSpreadsheet />
        );
      default:
        return <div>Unknown document type</div>;
    }
  };

  const renderSidebarContent = () => {
    const hasDocuments = Object.keys(groupedDocuments.yearlyDocuments).length > 0 ||
      (groupedDocuments.monthIndependent && groupedDocuments.monthIndependent.length > 0);

    if (!hasDocuments) {
      return (
        <div className="text-center p-4 text-muted-foreground">
          <FileIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No pay documents found</p>
        </div>
      );
    }

    return (
      <>
        {groupedDocuments.monthIndependent && groupedDocuments.monthIndependent.length > 0 && (
          <div className="mb-4 pb-4 border-b border-brand-border">
            <h3 className="px-2 py-1 text-sm font-medium text-muted-foreground uppercase tracking-wide">
              General Reports
            </h3>
            <div>
              {(() => {
                const modelEarningsReport = groupedDocuments.monthIndependent.filter(doc =>
                  doc.type === 'model-earnings-report'
                );
                const staffReports = groupedDocuments.monthIndependent.filter(doc =>
                  doc.type === 'staff-report' || doc.type === 'target-earnings'
                );
                const otherReports = groupedDocuments.monthIndependent.filter(doc =>
                  doc.type !== 'staff-report' && doc.type !== 'target-earnings' && doc.type !== 'model-earnings-report'
                );

                return (
                  <>
                    {otherReports.map(doc => {
                      const definition = getDocumentDefinition(doc.type);
                      return (
                        <button
                          key={doc.id}
                          onClick={() => handleDocumentSelect(doc)}
                          className={cn(
                            "flex items-center w-full px-3 py-2 text-left text-sm rounded-md hover:bg-brand-dark",
                            selectedDocument?.id === doc.id && "bg-brand-light text-brand-white border border-brand-border"
                          )}
                        >
                          <div className="flex items-center gap-2">
                            {definition?.icon}
                            {definition?.name}
                          </div>
                        </button>
                      );
                    })}
                    {modelEarningsReport.map(doc => {
                      const definition = getDocumentDefinition(doc.type);
                      return (
                        <button
                          key={doc.id}
                          onClick={() => handleDocumentSelect(doc)}
                          className={cn(
                            "flex items-center w-full px-3 py-2 text-left text-sm rounded-md hover:bg-brand-dark",
                            selectedDocument?.id === doc.id && "bg-brand-light text-brand-white border border-brand-border"
                          )}
                        >
                          <div className="flex items-center gap-2">
                            {definition?.icon}
                            {definition?.name}
                          </div>
                        </button>
                      );
                    })}
                    {staffReports.length > 0 && (
                      <div className="space-y-1 mt-3">
                        <h4 className="px-2 text-xs font-medium text-muted-foreground/80 uppercase tracking-wider">
                          Staff Performance
                        </h4>
                        <div className="space-y-1">
                          {staffReports.map(doc => {
                            const definition = getDocumentDefinition(doc.type);
                            return (
                              <button
                                key={doc.id}
                                onClick={() => handleDocumentSelect(doc)}
                                className={cn(
                                  "flex items-center w-full px-3 py-2 text-left text-sm rounded-md hover:bg-brand-dark ml-2",
                                  selectedDocument?.id === doc.id && "bg-brand-light text-brand-white border border-brand-border"
                                )}
                              >
                                <div className="flex items-center gap-2">
                                  {definition?.icon}
                                  {definition?.name}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {Object.keys(groupedDocuments.yearlyDocuments)
          .sort((a, b) => parseInt(b) - parseInt(a))
          .map(year => (
            <div key={year} className="mb-2">
              <button
                onClick={() => toggleYearExpanded(year)}
                className="flex items-center w-full px-2 py-2 text-left hover:bg-brand-dark rounded-md"
              >
                {expandedYears.has(year) ? (
                  <ChevronDown className="h-4 w-4 mr-2" />
                ) : (
                  <ChevronRight className="h-4 w-4 mr-2" />
                )}
                <Calendar className="h-4 w-4 mr-2" />
                <span className="font-medium">{year}</span>
              </button>

              {expandedYears.has(year) && (
                <div className="ml-4 space-y-1">
                  {Object.keys(groupedDocuments.yearlyDocuments[year])
                    .sort((a, b) => parseInt(b) - parseInt(a))
                    .map(month => {
                      const monthKey = `${year}-${month}`;
                      const isMonthExpanded = expandedMonths.has(monthKey);

                      return (
                        <div key={month} className="space-y-1">
                          <button
                            onClick={() => toggleMonthExpanded(year, month)}
                            className="flex items-center w-full px-2 py-1 text-left text-sm hover:bg-brand-dark rounded-md"
                          >
                            {isMonthExpanded ? (
                              <ChevronDown className="h-3 w-3 mr-2" />
                            ) : (
                              <ChevronRight className="h-3 w-3 mr-2" />
                            )}
                            <span className="font-medium text-muted-foreground">
                              {months[parseInt(month) - 1].label}
                            </span>
                          </button>

                          {isMonthExpanded && (
                            <div className="ml-5 space-y-1">
                              {Object.entries(groupedDocuments.yearlyDocuments[year][month].categories).map(([categoryName, categoryDocs]) => {
                                const categoryKey = `${year}-${month}-${categoryName}`;
                                const isCategoryExpanded = expandedCategories.has(categoryKey);

                                return (
                                  <div key={categoryName} className="space-y-1">
                                    <button
                                      onClick={() => toggleCategoryExpanded(year, month, categoryName)}
                                      className="flex items-center w-full px-2 py-1 text-left text-xs hover:bg-brand-dark rounded-md text-muted-foreground"
                                    >
                                      {isCategoryExpanded ? (
                                        <ChevronDown className="h-3 w-3 mr-2" />
                                      ) : (
                                        <ChevronRight className="h-3 w-3 mr-2" />
                                      )}
                                      <span className="font-medium uppercase tracking-wide">
                                        {categoryName}
                                      </span>
                                    </button>

                                    {isCategoryExpanded && (
                                      <div className="ml-5 space-y-1">
                                        {categoryDocs.map(doc => {
                                          const definition = getDocumentDefinition(doc.type);
                                          return (
                                            <button
                                              key={doc.id}
                                              onClick={() => handleDocumentSelect(doc)}
                                              className={cn(
                                                "flex items-center w-full px-3 py-2 text-left text-sm rounded-md hover:bg-brand-dark",
                                                selectedDocument?.id === doc.id && "bg-brand-light text-brand-white border border-brand-border"
                                              )}
                                            >
                                              <div className="flex items-center gap-2">
                                                {definition?.icon}
                                                {definition?.name}
                                              </div>
                                            </button>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}

                              {groupedDocuments.yearlyDocuments[year][month].uncategorized.map(doc => {
                                const definition = getDocumentDefinition(doc.type);
                                return (
                                  <button
                                    key={doc.id}
                                    onClick={() => handleDocumentSelect(doc)}
                                    className={cn(
                                      "flex items-center w-full px-3 py-2 text-left text-sm rounded-md hover:bg-brand-dark",
                                      selectedDocument?.id === doc.id && "bg-brand-light text-brand-white border border-brand-border"
                                    )}
                                  >
                                    <div className="flex items-center gap-2">
                                      {definition?.icon}
                                      {definition?.name}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          ))}
      </>
    );
  };

  const renderMainContent = () => {
    if (!selectedDocument) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center text-muted-foreground">
            <FileIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Select a document from the sidebar to view</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold">{selectedDocument.name}</h2>
          {selectedDocument.month !== undefined && selectedDocument.year !== undefined && (
            <p className="text-sm text-muted-foreground">
              {months[selectedDocument.month - 1].label} {selectedDocument.year}
            </p>
          )}
        </div>
        {renderSpreadsheetContent(selectedDocument)}
      </div>
    );
  };

  const breadcrumbs = useMemo(() => {
    const base = [
      { title: 'Home', href: '/' },
      { title: 'Payment Management', href: '/payment-files' }
    ];

    if (selectedDocument) {
      if (selectedDocument.isMonthIndependent) {
        base.push({
          title: selectedDocument.name,
          href: selectedDocument.type === 'staff-report' ? '/staff-report' : '/payment-files'
        });
      } else {
        base.push({
          title: `${selectedDocument.name} - ${months[selectedDocument.month! - 1].label} ${selectedDocument.year}`,
          href: `/payment-files/${selectedDocument.year}/${selectedDocument.month}/${selectedDocument.type}`
        });
      }
    }

    return base;
  }, [selectedDocument, months]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="space-y-1">
          <Breadcrumbs items={breadcrumbs} className="mb-2" />
          <h1 className="text-3xl font-bold tracking-tight text-brand-dark dark:text-foreground">
            Payment Management
          </h1>
          <p className="text-muted-foreground dark:text-muted-foreground">
            View and manage pay documents and summaries
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selectedDocument && getDocumentDefinition(selectedDocument.type)?.showFullscreen && (
            <Button
              variant="outline"
              size="sm"
              onClick={openFullscreen}
              className="flex items-center gap-2"
            >
              <Maximize2 className="h-4 w-4" />
              Fullscreen
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="flex h-full">
            <div className={cn(
              "border-r border-brand-border bg-brand-dark/10 flex flex-col transition-all duration-300 ease-in-out",
              sidebarCollapsed ? "w-12" : "w-80"
            )}>
              <div className="p-4 border-b border-brand-border flex items-center justify-between">
                {!sidebarCollapsed && (
                  <div className="flex-1">
                    <h2 className="text-lg font-semibold">Pay Documents</h2>
                    <p className="text-sm text-muted-foreground">Select a document to view</p>
                  </div>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleSidebar}
                  className={cn(
                    "h-8 w-8 p-0",
                    sidebarCollapsed && "mx-auto"
                  )}
                  title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                >
                  {sidebarCollapsed ? (
                    <PanelLeft className="h-4 w-4" />
                  ) : (
                    <PanelLeftClose className="h-4 w-4" />
                  )}
                </Button>
              </div>

              {!sidebarCollapsed && (
                <div className="flex-1 overflow-auto p-2">
                  {renderSidebarContent()}
                </div>
              )}
            </div>

            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-auto p-6">
                {renderMainContent()}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isFullscreenOpen} onOpenChange={setIsFullscreenOpen}>
        <VisuallyHidden>
          <DialogTitle>Payment Files</DialogTitle>
        </VisuallyHidden>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none border-none overflow-hidden p-0 flex flex-col">
          <div className="flex-1 min-h-0 flex">
            <div className={cn(
              "border-r border-brand-border bg-brand-dark/10 flex flex-col transition-all duration-300 ease-in-out",
              sidebarCollapsed ? "w-12" : "w-80"
            )}>
              <div className="p-4 border-b border-brand-border flex items-center justify-between">
                {!sidebarCollapsed && (
                  <div className="flex-1">
                    <h2 className="text-lg font-semibold">Pay Documents</h2>
                    <p className="text-sm text-muted-foreground">Select a document to view</p>
                  </div>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleSidebar}
                  className={cn(
                    "h-8 w-8 p-0",
                    sidebarCollapsed && "mx-auto"
                  )}
                  title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                >
                  {sidebarCollapsed ? (
                    <PanelLeft className="h-4 w-4" />
                  ) : (
                    <PanelLeftClose className="h-4 w-4" />
                  )}
                </Button>
              </div>
              {!sidebarCollapsed && (
                <div className="flex-1 overflow-auto p-2">
                  {renderSidebarContent()}
                </div>
              )}
            </div>
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-auto p-6">
                {selectedDocument ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-semibold">{selectedDocument.name}</h2>
                        {selectedDocument.month !== undefined && selectedDocument.year !== undefined && (
                          <p className="text-sm text-muted-foreground">
                            {months[selectedDocument.month - 1].label} {selectedDocument.year}
                          </p>
                        )}
                      </div>
                    </div>
                    {renderSpreadsheetContent(selectedDocument)}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-64">
                    <div className="text-center text-muted-foreground">
                      <FileIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Select a document from the sidebar to view</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
} 